/**
 * useCartInfo Hook
 * 
 * 职责：计算购物车信息提示（PolicyTips）
 * - 从 Store 读取配置和金额
 * - 计算当前需要显示的提示
 * - 管理自动轮播状态
 * 
 * 设计原则：
 * - 只负责计算，不负责文案渲染
 * - 所有金额保持 cents 单位
 * - 不硬编码业务规则，从配置读取
 * 
 * @version 3.1 - 发货优先版
 * - 移除所有免邮相关提示
 * - 发货时间提升为 Slot 1
 * - 新增手动切换方法
 */

import { useMemo, useState, useEffect, useCallback } from 'react';
import { useCartStore } from '../../store/useCartStore';
import { getNextShippingDate } from '../../utils/shippingCalculator';

export type CartInfoKind =
    | 'SHIPPING_ESTIMATE'   // Slot 1: 发货时间预估（优先级最高）
    | 'MIN_ORDER';          // Slot 2: 最低订单金额未达到

/**
 * 购物车提示信息对象
 */
export interface CartInfoTip {
    /** 唯一标识符 */
    id: string;
    /** 提示类型 */
    kind: CartInfoKind;
    /** 严重程度 */
    severity: 'warning' | 'info' | 'success';
    /** 距离目标的剩余金额 (分) */
    remainingCents?: number;
    /** 目标阈值金额 (分) */
    thresholdCents?: number;
    /** 预估发货日期 */
    shippingDate?: Date;
}

/**
 * useCartInfo Hook 的返回结果
 */
export interface UseCartInfoResult {
    /** 所有可用的提示列表 */
    tips: CartInfoTip[];
    /** 当前显示的提示 */
    currentTip: CartInfoTip | null;
    /** 当前提示的索引 */
    currentIndex: number;
    /** 提示总数 */
    totalTips: number;
    /** 切换到下一个提示 */
    nextTip: () => void;
    /** 切换到上一个提示 */
    prevTip: () => void;
}


/**
 * 计算购物车信息提示，包含最低订单与发货时间预估。
 * 
 * v3.1 变更：
 * - 移除免邮进度相关逻辑
 * - 发货时间作为 Slot 1（优先级最高）
 * - 支持手动切换（prevTip/nextTip）
 * 
 * @returns 轮播提示列表、当前提示及控制方法
 */
export function useCartInfo(): UseCartInfoResult {
    // 从 Store 读取数据
    const totalPriceCents = useCartStore((s) => s.total_price);
    const config = useCartStore((s) => s.config);
    const holidays = useCartStore((s) => s.holidays);

    // 计算提示列表（优先级顺序：SHIPPING_ESTIMATE > MIN_ORDER）
    const tips = useMemo((): CartInfoTip[] => {
        const result: CartInfoTip[] = [];

        // 空购物车不显示提示
        if (totalPriceCents === 0) {
            return result;
        }

        // Slot 1: 发货时间预估（购物车非空时始终显示）
        const nextDate = getNextShippingDate(holidays, config.cutoffHour);
        result.push({
            id: 'shipping-estimate',
            kind: 'SHIPPING_ESTIMATE',
            severity: 'success',
            shippingDate: nextDate,
        });

        // Slot 2: 最低订单金额检查
        if (totalPriceCents < config.minOrderCents) {
            result.push({
                id: 'min-order',
                kind: 'MIN_ORDER',
                severity: 'warning',
                thresholdCents: config.minOrderCents,
                remainingCents: config.minOrderCents - totalPriceCents
            });
        }

        return result;
    }, [totalPriceCents, config, holidays]);

    // 当前索引
    const [currentIndex, setCurrentIndex] = useState(0);

    // 当 tips 数组变化时，重置索引
    useEffect(() => {
        setCurrentIndex(0);
    }, [tips]);

    // 手动切换：下一个
    const nextTip = useCallback(() => {
        if (tips.length === 0) return;
        setCurrentIndex((prev) => (prev + 1) % tips.length);
    }, [tips.length]);

    // 手动切换：上一个
    const prevTip = useCallback(() => {
        if (tips.length === 0) return;
        setCurrentIndex((prev) => (prev - 1 + tips.length) % tips.length);
    }, [tips.length]);

    const currentTip = tips.length > 0 ? tips[currentIndex] : null;

    return {
        tips,
        currentTip,
        currentIndex,
        totalTips: tips.length,
        nextTip,
        prevTip,
    };
}
